/* ═══════════════════════════════════════════════════════════════
   PiARIS — Audit Report Exporters
   Generate self-contained HTML that works as:
     1. A downloadable .html file (universal, email-friendly)
     2. A .doc file that Microsoft Word opens natively

   Zero new dependencies. All CSS inline. All images embedded.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import { W, WCAG_KB } from "../constants";
import { sevToPriority, wcagToAffectedUsers, wcagToRiskLevel,
  selectorToComponent, isSitewideIssue, classifyFixCategory,
  fixCategoryLabel, globalIssueNumbers, computeComplianceScore } from "../helpers";

/* ─────────────────────────────────────────
   HTML ENCODING — escape user-supplied text
   ───────────────────────────────────────── */
function esc(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/* ─────────────────────────────────────────
   DOCUMENT-LEVEL CSS — print-friendly,
   Word-compatible, light-mode-only (reports
   should always be white paper aesthetic)
   ───────────────────────────────────────── */
var REPORT_CSS =
  "body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;background:#fff;margin:0;padding:32px 48px;line-height:1.5;font-size:12px}" +
  "h1{font-size:24px;font-weight:900;margin:0 0 6px;color:#0F172A}" +
  "h2{font-size:20px;font-weight:900;margin:24px 0 6px;color:#0F172A;border-bottom:2px solid #E2E5EF;padding-bottom:8px}" +
  "h3{font-size:14px;font-weight:800;margin:0 0 10px;color:#0F172A;line-height:1.35}" +
  "h4{font-size:11px;font-weight:800;margin:0 0 6px;color:#0F172A;text-transform:uppercase;letter-spacing:.04em}" +
  "p{margin:0 0 8px}" +
  "ul{margin:0 0 8px 0;padding-left:20px}" +
  "li{margin-bottom:2px}" +
  "a{color:#4F46E5;text-decoration:underline}" +
  "table{width:100%;border-collapse:collapse;margin:0 0 16px;font-size:11px}" +
  "th,td{border:1px solid #CBD5E1;padding:6px 10px;text-align:left;vertical-align:top}" +
  "th{background:#F1F3F9;font-weight:800;text-transform:uppercase;font-size:10px;letter-spacing:.04em}" +
  ".badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;border:1px solid}" +
  ".badge-p1{background:#FEF2F2;color:#DC2626;border-color:#FCA5A5}" +
  ".badge-p2{background:#FFFBEB;color:#D97706;border-color:#FCD34D}" +
  ".badge-p3{background:#ECFDF5;color:#059669;border-color:#A7F3D0}" +
  ".badge-open{background:#EFF6FF;color:#2563EB;border-color:#93C5FD}" +
  ".badge-done{background:#ECFDF5;color:#059669;border-color:#A7F3D0}" +
  ".badge-sitewide{background:#EEF2FF;color:#4F46E5;border:1px dashed #818CF8}" +
  ".badge-risk{background:#FEF2F2;color:#DC2626;border-color:#FCA5A5}" +
  ".issue{page-break-inside:avoid;border:1px solid #E2E5EF;border-radius:8px;padding:14px 18px;margin-bottom:12px}" +
  ".issue-title{font-size:13px;font-weight:800;color:#0F172A;line-height:1.35;margin-bottom:8px}" +
  ".issue-num{color:#94A3B8;font-weight:700;margin-right:4px}" +
  ".issue-meta{display:table;width:100%;border-collapse:collapse;margin-top:12px;border-top:1px solid #E2E5EF;padding-top:10px}" +
  ".meta-row{display:table-row}" +
  ".meta-label{display:table-cell;font-size:9px;font-weight:800;color:#94A3B8;text-transform:uppercase;letter-spacing:.04em;padding:3px 12px 3px 0;width:110px;vertical-align:top}" +
  ".meta-value{display:table-cell;font-size:11px;color:#1a1a1a;padding:3px 0;vertical-align:top}" +
  ".elements{background:#F8FAFC;border-left:3px solid #4F46E5;padding:8px 12px;margin:6px 0;font-family:'SF Mono',Consolas,monospace;font-size:10px;color:#0F172A;word-break:break-all}" +
  ".fix{font-size:11.5px;color:#334155;line-height:1.6;white-space:pre-wrap;margin:6px 0}" +
  ".related{display:inline-block;padding:6px 12px;border:1px solid #CBD5E1;background:#F8FAFC;border-radius:6px;font-size:11px;color:#4F46E5;text-decoration:none;margin:4px 4px 0 0}" +
  ".page-header{border-bottom:2px solid #0F172A;padding:10px 0 14px;margin-bottom:16px}" +
  ".page-header h2{margin:0 0 4px;border:none;padding:0}" +
  ".page-url{font-size:10px;color:#4F46E5;word-break:break-all}" +
  ".page-badges{margin-top:6px}" +
  ".summary{background:#F8FAFC;border:1px solid #E2E5EF;border-radius:10px;padding:20px 28px;margin-bottom:24px}" +
  ".summary h2{border:none;padding:0;margin:0 0 14px}" +
  ".summary-grid{display:table;width:100%;border-collapse:separate;border-spacing:10px 0}" +
  ".summary-cell{display:table-cell;width:25%;text-align:center;background:#fff;border:1px solid #E2E5EF;border-radius:8px;padding:14px 10px}" +
  ".summary-val{font-size:26px;font-weight:900;color:#0F172A;line-height:1}" +
  ".summary-lbl{font-size:9px;font-weight:800;color:#94A3B8;text-transform:uppercase;letter-spacing:.04em;margin-top:4px}" +
  ".cover{text-align:center;padding:40px 20px;border-bottom:2px solid #E2E5EF;margin-bottom:32px}" +
  ".cover-title{font-size:32px;font-weight:900;color:#0F172A;margin:0 0 8px}" +
  ".cover-sub{font-size:14px;color:#475569;margin:0 0 16px}" +
  ".cover-domain{font-size:16px;color:#4F46E5;font-weight:700;margin:8px 0}" +
  ".cover-meta{font-size:10px;color:#64748B;margin-top:16px}" +
  ".footer{margin-top:48px;padding-top:16px;border-top:1px solid #E2E5EF;font-size:9px;color:#94A3B8;text-align:center}" +
  ".about{background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:12px 16px;margin:20px 0;font-size:10px;color:#78350F;line-height:1.55}" +
  "@media print{body{padding:20px 32px}.issue{page-break-inside:avoid}.page-header{page-break-after:avoid}}";

/* ─────────────────────────────────────────
   SINGLE ISSUE → HTML
   ───────────────────────────────────────── */
function renderIssueHtml(iss, num, templateStats) {
  var wcag = iss.wcag || "";
  var wcagInfo = W[wcag] || {};
  var kbInfo = WCAG_KB[wcag] || {};
  var pri = sevToPriority(iss.sev || "major");
  var sitewide = isSitewideIssue(iss, templateStats);
  var affected = wcagToAffectedUsers(wcag);
  var risk = wcagToRiskLevel(iss.sev, wcagInfo.l);
  var category = fixCategoryLabel(classifyFixCategory(iss));
  var component = selectorToComponent(iss.selector, iss.el);
  var isOpen = !iss.remediated;
  var elementProse = iss.selector || (iss.el || "").substring(0, 200) || "\u2014";
  var fixProse = iss.fix || wcagInfo.r || kbInfo.fix || "No remediation guidance available for this issue type.";

  var badges = [];
  badges.push('<span class="badge ' + (isOpen ? "badge-open" : "badge-done") + '">' + (isOpen ? "OPEN" : "REMEDIATED") + '</span>');
  badges.push('<span class="badge badge-' + pri.code.toLowerCase() + '">' + pri.label.toUpperCase() + ' (' + pri.code + ')</span>');
  if (sitewide) badges.push('<span class="badge badge-sitewide">SITEWIDE</span>');
  if (risk === "High Risk") badges.push('<span class="badge badge-risk">\u26A0 HIGH RISK</span>');

  var html = '<article class="issue">';
  html += '<h3 class="issue-title"><span class="issue-num">#' + num + ':</span> ' + esc(iss.desc || wcagInfo.n || "Accessibility issue") + '</h3>';
  html += '<div>' + badges.join(" ") + '</div>';

  /* Per-issue screenshots (manual uploads + extension auto-crops). Rendered
     as a responsive row of inline images so the exported HTML stays
     self-contained for email/print. Word opens HTML with <img src="https://…">
     and fetches images on first open; if offline, the auditor's Word shows
     the alt text and a broken-image placeholder — acceptable degradation. */
  if (Array.isArray(iss.screenshots) && iss.screenshots.length > 0) {
    html += '<div style="margin-top:10px"><h4>Screenshots</h4>';
    html += '<div style="display:block">';
    for (var si = 0; si < iss.screenshots.length; si++) {
      var s = iss.screenshots[si];
      if (!s || !s.url) continue;
      var alt = s.caption ? esc(s.caption) : ('Screenshot ' + (si + 1) + ' for issue #' + num);
      html += '<figure style="display:inline-block;margin:0 8px 8px 0;vertical-align:top;border:1px solid #E2E5EF;border-radius:6px;padding:4px;max-width:320px">';
      html += '<img src="' + esc(s.url) + '" alt="' + alt + '" style="max-width:100%;display:block;border-radius:4px">';
      if (s.caption) html += '<figcaption style="font-size:9px;color:#64748B;padding:4px 2px 0;line-height:1.3">' + esc(s.caption) + '</figcaption>';
      html += '</figure>';
    }
    html += '</div></div>';
  }

  if (component.name) {
    html += '<div style="margin-top:10px;font-size:11px;color:#475569">';
    html += '<strong style="text-transform:uppercase;font-size:9px;letter-spacing:.04em;color:#0F172A">Component:</strong> ';
    html += esc(component.name);
    if (component.kind) html += ' <span style="color:#94A3B8">(' + esc(component.kind) + (sitewide ? ', sitewide' : '') + ')</span>';
    html += '</div>';
  }

  html += '<div style="margin-top:10px"><h4>Elements</h4>';
  html += '<div class="elements">' + esc(elementProse);
  if (iss.count && iss.count > 1) html += ' <span style="color:#94A3B8">(\u00D7' + iss.count + ')</span>';
  html += '</div></div>';

  html += '<div style="margin-top:10px"><h4>Fix</h4>';
  html += '<div class="fix">' + esc(fixProse) + '</div></div>';

  if (kbInfo.url) {
    html += '<div style="margin-top:10px"><h4>Related Articles</h4>';
    html += '<a class="related" href="' + esc(kbInfo.url) + '" target="_blank" rel="noopener">';
    html += '\uD83D\uDCC4 ' + esc((kbInfo.n || wcag) + ' \u2014 W3C Understanding') + '</a>';
    html += '</div>';
  }

  /* Metadata table */
  html += '<div class="issue-meta">';
  html += '<div class="meta-row"><div class="meta-label">Priority</div><div class="meta-value"><span class="badge badge-' + pri.code.toLowerCase() + '">' + pri.label.toUpperCase() + ' (' + pri.code + ')</span></div></div>';
  if (risk === "High Risk") html += '<div class="meta-row"><div class="meta-label">Risk</div><div class="meta-value"><strong style="color:#DC2626">\u26A0 ' + risk + '</strong></div></div>';
  html += '<div class="meta-row"><div class="meta-label">Category</div><div class="meta-value">' + esc(category) + '</div></div>';
  html += '<div class="meta-row"><div class="meta-label">Affected Users</div><div class="meta-value">' + affected.map(esc).join(" \u00B7 ") + '</div></div>';
  if (wcag) {
    html += '<div class="meta-row"><div class="meta-label">Guidelines</div><div class="meta-value">';
    html += '<strong>' + esc(wcag) + (wcagInfo.l ? ' (WCAG 2.1 ' + esc(wcagInfo.l) + ')' : '') + '</strong>';
    if (wcagInfo.n) html += '<br><span style="color:#64748B;font-size:10px">' + esc(wcagInfo.n) + '</span>';
    html += '</div></div>';
  }
  html += '<div class="meta-row"><div class="meta-label">Client Status</div><div class="meta-value">' + (isOpen ? 'Open' : 'Resolved') + '</div></div>';
  html += '</div>';

  html += '</article>';
  return html;
}

/* ─────────────────────────────────────────
   FULL DOCUMENT BUILDER
   ───────────────────────────────────────── */
function buildAuditHtml(wp, dc, meta) {
  var templateStats = meta.templateStats || { groups: {}, templateGroupCount: 0 };
  var comp = computeComplianceScore(wp || [], dc || [], W);
  var numbers = globalIssueNumbers(wp || []);
  var totalIssues = (wp || []).reduce(function(x, p) { return x + ((p.issues || []).length); }, 0);
  var critCount = 0, majCount = 0, minCount = 0;
  (wp || []).forEach(function(pg) {
    (pg.issues || []).forEach(function(iss) {
      if (iss.sev === "critical") critCount++;
      else if (iss.sev === "major") majCount++;
      else minCount++;
    });
  });

  var domain = meta.domain || "";
  var scanDate = meta.scanDate || new Date().toLocaleDateString();
  var sessionId = meta.sessionId || "";

  var html = '<!DOCTYPE html>';
  html += '<html lang="en"><head><meta charset="utf-8">';
  html += '<title>PiARIS Audit Report \u2014 ' + esc(domain) + '</title>';
  html += '<style>' + REPORT_CSS + '</style>';
  html += '</head><body>';

  /* Cover */
  html += '<div class="cover">';
  html += '<h1 class="cover-title">Accessibility Audit Report</h1>';
  html += '<p class="cover-sub">Pre-Remediation Findings \u00B7 WCAG 2.1 Level AA</p>';
  html += '<div class="cover-domain">' + esc(domain) + '</div>';
  html += '<p class="cover-meta">Generated ' + esc(scanDate) + (sessionId ? ' \u00B7 Session ' + esc(sessionId) : '') + ' \u00B7 PiARIS&trade; by Pi Consultants</p>';
  html += '</div>';

  /* Executive Summary */
  html += '<section class="summary" aria-label="Executive Summary">';
  html += '<h2>Executive Summary</h2>';
  html += '<div class="summary-grid">';
  html += '<div class="summary-cell"><div class="summary-val">' + ((wp && wp.length) || 0).toLocaleString() + '</div><div class="summary-lbl">Pages Audited</div></div>';
  html += '<div class="summary-cell"><div class="summary-val">' + totalIssues.toLocaleString() + '</div><div class="summary-lbl">Total Findings</div></div>';
  html += '<div class="summary-cell"><div class="summary-val" style="color:#DC2626">' + critCount.toLocaleString() + '</div><div class="summary-lbl">Critical (P1)</div></div>';
  html += '<div class="summary-cell"><div class="summary-val">' + (comp.activeScore != null ? comp.activeScore + "%" : "\u2014") + '</div><div class="summary-lbl">Compliance Score</div></div>';
  html += '</div>';

  /* Severity breakdown table */
  html += '<table style="margin-top:16px"><thead><tr><th>Severity</th><th>Count</th><th>Description</th></tr></thead><tbody>';
  html += '<tr><td><span class="badge badge-p1">CRITICAL (P1)</span></td><td>' + critCount.toLocaleString() + '</td><td>Blocking barriers. Cannot be conformant until resolved.</td></tr>';
  html += '<tr><td><span class="badge badge-p2">SERIOUS (P2)</span></td><td>' + majCount.toLocaleString() + '</td><td>Significant barriers. High priority remediation.</td></tr>';
  html += '<tr><td><span class="badge badge-p3">WARNING (P3)</span></td><td>' + minCount.toLocaleString() + '</td><td>Minor issues. Should be addressed for full compliance.</td></tr>';
  html += '</tbody></table>';
  html += '<div class="about">';
  html += '<strong>About this report:</strong> This pre-remediation audit is generated from automated scans (axe-core + HTML_CodeSniffer) and the PiARIS WCAG knowledge base. Component classification is heuristic and based on CSS selectors. Screenshots are DOM captures from the scan session. For a legally defensible Letter of Conformance, a human audit pass is recommended to verify findings and component boundaries.';
  html += '</div>';
  html += '</section>';

  /* Per-page sections */
  (wp || []).forEach(function(pg, pi) {
    if (!pg.issues || pg.issues.length === 0) return;
    html += '<section class="page-section" aria-label="' + esc(pg.n || "Page") + '">';
    html += '<div class="page-header">';
    html += '<h2>' + esc(pg.n || "Untitled Page") + '</h2>';
    if (pg.url) html += '<div class="page-url">' + esc(pg.url) + '</div>';
    html += '<div class="page-badges">';
    html += '<span class="badge" style="background:#FEF2F2;color:#DC2626;border-color:#FCA5A5">' + pg.issues.length + ' issues</span>';
    if (pg.critCount > 0) html += ' <span class="badge badge-p1">' + pg.critCount + ' critical</span>';
    if (pg.isArchived) html += ' <span class="badge" style="background:#FFFBEB;color:#D97706;border-color:#FCD34D">\uD83D\uDCE6 Archived</span>';
    html += '</div>';
    html += '</div>';
    pg.issues.forEach(function(iss, ii) {
      var num = numbers[pi + "_" + ii];
      html += renderIssueHtml(iss, num, templateStats);
    });
    html += '</section>';
  });

  /* Footer */
  html += '<div class="footer">';
  html += '&copy; ' + new Date().getFullYear() + ' Pi Consultants &middot; pi-consulting.us &middot; PiARIS&trade; Accessibility Platform &middot; WCAG 2.1 Level AA';
  html += '</div>';

  html += '</body></html>';
  return html;
}

/* ─────────────────────────────────────────
   DOWNLOAD HELPERS
   ───────────────────────────────────────── */
function triggerDownload(content, filename, mimeType) {
  var blob = new Blob([content], { type: mimeType });
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function() {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function sanitizeFilename(s) {
  return String(s || "domain").replace(/[^a-zA-Z0-9.-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

/** Download as standalone HTML file. */
function downloadAuditHtml(wp, dc, meta) {
  var html = buildAuditHtml(wp, dc, meta);
  var fn = "piaris-audit-" + sanitizeFilename(meta.domain) + "-" + (new Date().toISOString().slice(0, 10)) + ".html";
  triggerDownload(html, fn, "text/html;charset=utf-8");
}

/** Download as Word document (HTML-based .doc — Word opens it natively).
    This is a common compatibility trick and requires zero dependencies. */
function downloadAuditDoc(wp, dc, meta) {
  var html = buildAuditHtml(wp, dc, meta);
  /* Word-specific prefix helps older Word versions recognize it */
  var wordHtml = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">' +
                 html.replace(/^<!DOCTYPE[^>]*>\s*<html[^>]*>/i, "");
  var fn = "piaris-audit-" + sanitizeFilename(meta.domain) + "-" + (new Date().toISOString().slice(0, 10)) + ".doc";
  triggerDownload(wordHtml, fn, "application/msword");
}

export { buildAuditHtml, downloadAuditHtml, downloadAuditDoc };
